import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { ImageLightbox } from '../components/ImageLightbox';
import { 
  ArrowLeft, 
  ArrowRight,
  User,
  BadgeCheck,
  MessageCircle,
  Tag,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Send,
  Expand,
  Clock,
  ChevronRight as BreadcrumbIcon,
  X,
  Bookmark,
  Share2,
  Flag,
  Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ReportModal } from '../components/ReportModal';
import { addToRecentlyViewed } from '../utils/recentlyViewed';
import { trackEvent } from '../utils/tracking';
import { toast } from '../lib/toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to normalize image URLs
// Handles both string URLs and new object format {ref, url, expires_at}
const normalizeImageUrl = (url) => {
  if (!url) return null;
  // Handle new image object format
  if (typeof url === 'object' && url.url) {
    return url.url;
  }
  // Handle string URLs
  if (typeof url !== 'string') return null;
  if (url.startsWith('https://images.unsplash.com')) return url;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) {
    return `${API_URL}${url}`;
  }
  if (url.includes('/api/uploads/')) {
    const match = url.match(/\/api\/uploads\/.+$/);
    if (match) {
      return `${API_URL}${match[0]}`;
    }
  }
  return url;
};

const CATEGORY_IMAGES = {
  electronics: 'https://images.unsplash.com/photo-1750797308931-b0d261abb3d5?w=400&h=300&fit=crop',
  furniture: 'https://images.unsplash.com/photo-1673695008224-fa5a9d195dfb?w=400&h=300&fit=crop',
  other: 'https://images.unsplash.com/photo-1586864387967-d02ef85d93e8?w=400&h=300&fit=crop',
};

// Format date for display
const formatDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
  });
};

// Format relative time
const formatRelativeTime = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return formatDate(dateString);
};

// Category labels
const CATEGORY_LABELS = {
  electronics: 'Electronics',
  furniture: 'Furniture',
  clothing: 'Clothing',
  vehicles: 'Vehicles',
  sports: 'Sports & Outdoors',
  home: 'Home & Garden',
  collectibles: 'Collectibles',
  books: 'Books & Media',
  tools: 'Tools & Equipment',
  camera: 'Camera & Photo',
  other: 'Other'
};

// PPSR section — pending (launch default) renders as a "coming soon" button,
// while cleared/flagged/error render the full status badge.
// See backend/integrations/ppsr/client.py for the status sentinel set.
const PPSRSection = ({ summary }) => {
  const status = (summary && summary.status) || 'pending';

  if (status === 'pending') {
    return (
      <button
        type="button"
        disabled
        className="w-full mt-3 h-11 rounded-full border border-[var(--theme-border)] bg-[var(--theme-hover)] text-sm font-semibold text-[var(--theme-text-secondary)] cursor-not-allowed flex items-center justify-center gap-2"
        title="A full PPSR check will be bundled with Plus listings once the service is activated."
        data-testid="ppsr-request-btn"
      >
        <BadgeCheck className="w-4 h-4" />
        Request PPSR (Coming Soon)
      </button>
    );
  }

  const CONFIG = {
    cleared: {
      bg: 'bg-emerald-50 dark:bg-emerald-900/20',
      border: 'border-emerald-300 dark:border-emerald-700',
      text: 'text-emerald-800 dark:text-emerald-200',
      dot: 'bg-emerald-500',
      title: 'PPSR check cleared',
      body: 'No finance owing · Not written off · Not reported stolen',
    },
    flagged: {
      bg: 'bg-rose-50 dark:bg-rose-900/20',
      border: 'border-rose-300 dark:border-rose-700',
      text: 'text-rose-800 dark:text-rose-200',
      dot: 'bg-rose-500',
      title: 'PPSR check — adverse findings',
      body: null,
    },
    error: {
      bg: 'bg-amber-50 dark:bg-amber-900/20',
      border: 'border-amber-300 dark:border-amber-700',
      text: 'text-amber-800 dark:text-amber-200',
      dot: 'bg-amber-500',
      title: 'PPSR check unavailable',
      body: summary.message || 'The PPSR service could not be reached. The listing is still valid — please verify independently before purchase.',
    },
  };
  const cfg = CONFIG[status] || CONFIG.error;

  const flaggedLines = [];
  if (status === 'flagged') {
    if (summary.has_encumbrances) {
      flaggedLines.push(`Finance owing (${summary.encumbrance_count || 1} encumbrance${(summary.encumbrance_count || 1) > 1 ? 's' : ''})`);
    }
    if (summary.wovr && summary.wovr !== 'none') {
      flaggedLines.push(`Written off — ${summary.wovr} repair`);
    }
    if (summary.stolen) {
      flaggedLines.push('Reported stolen');
    }
  }

  return (
    <div className={`rounded-xl border ${cfg.border} ${cfg.bg} p-4`}>
      <div className="flex items-start gap-3">
        <div className={`mt-1 w-2.5 h-2.5 rounded-full ${cfg.dot} flex-shrink-0`} />
        <div className="flex-1 min-w-0">
          <p className={`font-semibold ${cfg.text}`}>{cfg.title}</p>
          {cfg.body && (
            <p className={`text-sm mt-1 ${cfg.text} opacity-90`}>{cfg.body}</p>
          )}
          {flaggedLines.length > 0 && (
            <ul className={`text-sm mt-1 space-y-0.5 ${cfg.text}`}>
              {flaggedLines.map((line, i) => (
                <li key={i}>• {line}</li>
              ))}
            </ul>
          )}
          <p className="text-xs mt-2 text-[var(--theme-text-muted)]">
            PPSR check is informational only. WANTD does not guarantee vehicle title or condition.{' '}
            <Link to="/auto/terms" className="underline hover:no-underline">See Auto T&amp;Cs</Link>.
          </p>
        </div>
      </div>
    </div>
  );
};

// Vehicle details card — renders for category === "vehicles".
// VIN is never returned by the public API (see sanitize_vehicle_details_for_public);
// rego is masked to the last 2 chars if present.
const VehicleDetailsCard = ({ vehicleDetails }) => {
  if (!vehicleDetails) return null;
  const vd = vehicleDetails;

  const rows = [
    ['Make', vd.make],
    ['Model', vd.model],
    ['Year', vd.year],
    ['Kilometres', vd.km != null ? `${Number(vd.km).toLocaleString()} km` : null],
    ['Transmission', vd.transmission],
    ['Fuel', vd.fuel_type],
    ['Body', vd.body_type],
    ['Colour', vd.color],
    ['Build date', vd.build_date],
    ['Compliance date', vd.compliance_date],
    ['Owners', vd.owners_count],
    ['Service history', vd.service_history],
    ['WOVR status', vd.wovr_status],
    ['Rego', vd.rego ? `${vd.rego}${vd.rego_state ? ` / ${vd.rego_state}` : ''}` : null],
    ['Rego expiry', vd.rego_expiry],
  ].filter(([_, v]) => v !== null && v !== undefined && v !== '');

  const tier = vd.listing_tier || 'basic';

  return (
    <div className="space-y-4">
      {/* Vehicle spec table */}
      {rows.length > 0 && (
        <div className="p-4 rounded-xl bg-[var(--theme-card)] border border-[var(--theme-border)]">
          <h3 className="font-semibold text-[var(--theme-text-primary)] mb-3">Vehicle details</h3>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
            {rows.map(([label, value]) => (
              <div key={label}>
                <p className="text-xs text-[var(--theme-text-muted)] uppercase tracking-wide">{label}</p>
                <p className="text-sm font-medium text-[var(--theme-text-primary)] capitalize">{String(value)}</p>
              </div>
            ))}
          </div>
          {(vd.modifications || vd.rwc_notes || vd.gas_cert_notes) && (
            <div className="mt-3 pt-3 border-t border-[var(--theme-border)] space-y-2">
              {vd.modifications && (
                <div>
                  <p className="text-xs text-[var(--theme-text-muted)] uppercase tracking-wide">Modifications</p>
                  <p className="text-sm text-[var(--theme-text-secondary)]">{vd.modifications}</p>
                </div>
              )}
              {vd.rwc !== undefined && vd.rwc !== null && (
                <div>
                  <p className="text-xs text-[var(--theme-text-muted)] uppercase tracking-wide">Roadworthy certificate</p>
                  <p className="text-sm text-[var(--theme-text-secondary)]">
                    {vd.rwc ? 'Yes' : 'No'}{vd.rwc_notes ? ` — ${vd.rwc_notes}` : ''}
                  </p>
                </div>
              )}
              {vd.gas_cert_notes && (
                <div>
                  <p className="text-xs text-[var(--theme-text-muted)] uppercase tracking-wide">Gas certificate</p>
                  <p className="text-sm text-[var(--theme-text-secondary)]">{vd.gas_cert_notes}</p>
                </div>
              )}
            </div>
          )}

          {/* PPSR — request button (pending) or status badge (cleared/flagged/error) */}
          {tier === 'plus' && <PPSRSection summary={vd.ppsr_summary} />}
        </div>
      )}
    </div>
  );
};

export default function CatalogItemDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, api } = useAuth();

  const [item, setItem] = useState(null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isSaved, setIsSaved] = useState(false);
  const [savingItem, setSavingItem] = useState(false);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Report modal state
  const [showReportModal, setShowReportModal] = useState(false);

  // Offer state
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offerAmount, setOfferAmount] = useState('');
  const [offerSubmitting, setOfferSubmitting] = useState(false);
  const [myOffer, setMyOffer] = useState(null); // active offer by this buyer on this item

  useEffect(() => {
    fetchItemDetails();
  }, [id]);
  
  // Record view when item is loaded (once per session per item)
  useEffect(() => {
    if (!item?.id) return;
    
    // Use sessionStorage to track viewed items (prevents counting same user multiple times)
    const viewedKey = `viewed_item_${item.id}`;
    if (sessionStorage.getItem(viewedKey)) return;
    
    // Record the view
    axios.post(`${API_URL}/api/inventory/${item.id}/view`)
      .then(() => {
        sessionStorage.setItem(viewedKey, 'true');
      })
      .catch(() => {
        // Silent fail - view counting is not critical
      });
  }, [item?.id]);

  // Track recently viewed when item loads (only if not user's own listing)
  useEffect(() => {
    if (item && user && item.user_id !== user.id) {
      addToRecentlyViewed(item, 'inventory');
    }
  }, [item, user]);

  // Dwell tracking — fire after 2s on page, report actual duration on unmount
  useEffect(() => {
    if (!item?.id) return;
    const startTime = Date.now();
    const DWELL_THRESHOLD_MS = 2000;
    let qualified = false;
    const timer = setTimeout(() => { qualified = true; }, DWELL_THRESHOLD_MS);
    return () => {
      clearTimeout(timer);
      if (qualified) {
        trackEvent('dwell', {
          item_id: item.id,
          category: item.category,
          subcategory: item.subcategory,
          duration_ms: Date.now() - startTime,
        });
      }
    };
  }, [item?.id]);

  // Check if item is saved
  useEffect(() => {
    if (!item?.id || !user) return;
    
    api.get(`/inventory/${item.id}/is-saved`)
      .then(res => setIsSaved(res.data.is_saved))
      .catch(() => setIsSaved(false));
  }, [item?.id, user]);

  const fetchItemDetails = async () => {
    setLoading(true);
    try {
      // Fetch single item from public catalog endpoint
      const itemRes = await axios.get(`${API_URL}/api/inventory/catalog/${id}`);
      const foundItem = itemRes.data;

      if (!foundItem) {
        toast.error('Item not found');
        navigate('/');
        return;
      }

      setItem(foundItem);

      // Fetch seller info
      try {
        const sellerRes = await axios.get(`${API_URL}/api/users/profile/${foundItem.user_id}`);
        setSeller(sellerRes.data);
      } catch (e) {
        // Seller info not critical
        console.log('Could not fetch seller info');
      }

      // Fetch buyer's active offer on this item (if logged in and not owner)
      if (user && user.id !== foundItem.user_id) {
        try {
          const offersRes = await api.get(`/offers/item/${foundItem.id}`);
          const active = (offersRes.data.offers || []).find(
            o => o.buyer_id === user.id && ['pending', 'countered', 'accepted'].includes(o.status)
          );
          setMyOffer(active || null);
        } catch (e) {
          // Offers not critical
        }
      }
    } catch (error) {
      console.error('Failed to fetch item:', error);
      toast.error('Failed to load item');
    } finally {
      setLoading(false);
    }
  };

  const nextImage = () => {
    if (item?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % item.images.length);
    }
  };

  const prevImage = () => {
    if (item?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + item.images.length) % item.images.length);
    }
  };

  const handleSaveToggle = async () => {
    if (!user) {
      toast.error('Please sign in to save listings');
      return;
    }
    
    if (item?.user_id === user.id) {
      toast.error('Cannot save your own listing');
      return;
    }
    
    setSavingItem(true);
    try {
      if (isSaved) {
        await api.delete(`/inventory/${item.id}/save`);
        setIsSaved(false);
        toast.success('Removed from saved items');
      } else {
        await api.post(`/inventory/${item.id}/save`);
        setIsSaved(true);
        // Fire save tracking event to feed interest vector
        trackEvent('save', { item_id: item.id, category: item.category, subcategory: item.subcategory });
        toast.success('Item saved!', {
          action: {
            label: 'View Saved',
            onClick: () => navigate('/saved')
          }
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update saved items');
    } finally {
      setSavingItem(false);
    }
  };

  const handleSubmitOffer = async () => {
    if (!user) {
      toast.error('Please sign in to make an offer');
      navigate('/login');
      return;
    }
    const cents = Math.round(parseFloat(offerAmount) * 100);
    if (!cents || cents <= 0) {
      toast.error('Please enter a valid offer amount');
      return;
    }
    setOfferSubmitting(true);
    try {
      const res = await api.post('/offers', { item_id: item.id, amount_cents: cents });
      setMyOffer(res.data);
      setShowOfferModal(false);
      setOfferAmount('');
      toast.success("Offer sent! You'll be notified when the seller responds.");
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail.toLowerCase().includes('already have an active offer')) {
        // Offer persisted on a previous attempt despite a server error — fetch and surface it
        try {
          const offersRes = await api.get(`/offers/item/${item.id}`);
          const existing = (offersRes.data.offers || []).find(
            o => o.buyer_id === user.id && ['pending', 'countered', 'accepted'].includes(o.status)
          );
          if (existing) {
            setMyOffer(existing);
            setShowOfferModal(false);
            setOfferAmount('');
            toast.success("Your offer is already active — the seller will be notified.");
            return;
          }
        } catch (_) { /* fall through to generic error */ }
      }
      toast.error(detail || 'Failed to submit offer');
    } finally {
      setOfferSubmitting(false);
    }
  };

  const handleWithdrawOffer = async () => {
    if (!myOffer) return;
    try {
      await api.post(`/offers/${myOffer.id}/withdraw`);
      setMyOffer(null);
      toast.success('Offer withdrawn');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw offer');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-32 bg-[var(--theme-hover)] rounded" />
            <div className="aspect-[4/3] bg-[var(--theme-hover)] rounded-xl" />
            <div className="space-y-3">
              <div className="h-8 w-3/4 bg-[var(--theme-hover)] rounded" />
              <div className="h-4 w-1/2 bg-[var(--theme-hover)] rounded" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!item) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <h1 className="text-2xl font-bold text-[var(--theme-text-primary)]">Item not found</h1>
          <p className="text-[var(--theme-text-secondary)] mt-2">This item may have been sold or removed.</p>
          <Button onClick={() => navigate('/')} className="mt-4">
            Back to Home
          </Button>
        </div>
      </Layout>
    );
  }

  const images = item.images?.length > 0 
    ? item.images.map(normalizeImageUrl) 
    : [CATEGORY_IMAGES[item.category] || CATEGORY_IMAGES.other];
  
  const price = item.price != null ? item.price : item.estimated_value;
  const isFree = item.is_free || price === 0;
  const isEoi = !!item.is_eoi;
  const conditionLabel = item.condition?.replace('_', ' ');
  const categoryLabel = CATEGORY_LABELS[item.category] || item.category;
  
  // Get member since year
  const getMemberYear = () => {
    const createdAt = seller?.created_at || item.seller_created_at;
    if (createdAt) {
      return new Date(createdAt).getFullYear();
    }
    return null;
  };
  const memberYear = getMemberYear();

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4">
        {/* Breadcrumbs and Actions Row */}
        <div className="flex items-center justify-between py-3 flex-shrink-0">
          {/* Back arrow + Breadcrumbs */}
          <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 rounded-full hover:bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] transition-colors flex-shrink-0"
            title="Go back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <nav className="flex items-center gap-2 text-sm">
            <Link 
              to="/" 
              className="px-3 py-1 rounded-full bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] hover:bg-[var(--theme-border)] hover:text-[var(--theme-text-primary)] transition-colors"
            >
              Home
            </Link>
            <BreadcrumbIcon className="w-3.5 h-3.5 text-[var(--theme-text-muted)]" />
            <Link 
              to={`/?category=${item.category}`} 
              className="px-3 py-1 rounded-full bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] hover:bg-[var(--theme-border)] hover:text-[var(--theme-text-primary)] transition-colors"
            >
              {categoryLabel}
            </Link>
            <BreadcrumbIcon className="w-3.5 h-3.5 text-[var(--theme-text-muted)]" />
            <span className="px-3 py-1 rounded-full bg-[#1E3A5F]/10 text-[var(--theme-text-primary)] font-medium truncate max-w-[250px]">
              {item.title}
            </span>
          </nav>
          </div>

          {/* Action Buttons with Views */}
          <div className="flex items-center gap-2">
            {/* Views counter */}
            <span className="flex items-center gap-1.5 text-sm text-[var(--theme-text-muted)] mr-2">
              <Eye className="w-4 h-4" />
              {item.views || 0}
            </span>
            
            <button
              onClick={handleSaveToggle}
              disabled={savingItem}
              className={`p-2 rounded-full hover:bg-[var(--theme-hover)] transition-colors ${
                isSaved 
                  ? 'text-[#C9956C]' 
                  : 'text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)]'
              }`}
              title={isSaved ? 'Remove from saved' : 'Save listing'}
              data-testid="save-listing-btn"
            >
              <Bookmark className={`w-5 h-5 ${isSaved ? 'fill-current' : ''}`} />
            </button>
            <button
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                toast.success('Link copied to clipboard!');
              }}
              className="p-2 rounded-full hover:bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] transition-colors"
              title="Share listing"
              data-testid="share-listing-btn"
            >
              <Share2 className="w-5 h-5" />
            </button>
            <button
              onClick={() => {
                if (!user) {
                  toast.error('Please sign in to report listings');
                  return;
                }
                setShowReportModal(true);
              }}
              className="p-2 rounded-full hover:bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] hover:text-red-500 transition-colors"
              title="Report listing"
              data-testid="report-listing-btn"
            >
              <Flag className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Mobile: flex-col (info first, then images), Desktop: grid 2 cols (images left, info right) */}
        <div className="flex flex-col lg:grid lg:grid-cols-2 gap-6 lg:gap-8">
          
          {/* Item Details - Shows FIRST on mobile, SECOND (right column) on desktop */}
          <div className="order-1 lg:order-2 space-y-5">
            {/* Price and Title */}
            <div>
              <h1 className="text-2xl font-bold text-[var(--theme-text-primary)] mb-3" data-testid="item-title">
                {item.title}
              </h1>
              <div className="flex items-center gap-3 flex-wrap">
                {isEoi ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="text-3xl font-bold text-[#1E3A5F]" data-testid="item-price">EOI</span>
                    <span className="px-3 py-1 rounded-full bg-[#1E3A5F] text-white text-xs font-bold">EXPRESSION OF INTEREST</span>
                  </span>
                ) : isFree ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="text-3xl font-bold text-[#059669]" data-testid="item-price">FREE</span>
                    <span className="px-3 py-1 rounded-full bg-[#059669] text-white text-xs font-bold">FREE LISTING</span>
                  </span>
                ) : (
                  <span className="text-3xl font-bold text-[#C9956C]" data-testid="item-price">
                    ${price?.toLocaleString()}
                  </span>
                )}
                {!isFree && (item.is_firm ? (
                  <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-[#1E3A5F] text-white text-xs font-semibold">
                    <Tag className="w-3 h-3" />
                    FIRM PRICE
                  </span>
                ) : (
                  <span className="px-3 py-1 rounded-full bg-[#059669]/10 text-[#059669] text-xs font-semibold">
                    NEGOTIABLE
                  </span>
                ))}
              </div>
              {isFree && item.expires_at && (
                <p className="mt-1 text-xs text-[var(--theme-text-muted)] flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Expires {new Date(item.expires_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              )}
            </div>

            {/* Seller + Description Box */}
            <div className="p-4 rounded-xl bg-[var(--theme-card)] border border-[var(--theme-border)]">
              {/* Seller row - Clickable */}
              <Link 
                to={`/user/${item.user_id}`}
                className="flex items-center gap-3 mb-4 pb-4 border-b border-[var(--theme-border)] hover:bg-[var(--theme-hover)] -mx-4 px-4 py-2 -my-2 rounded-t-xl transition-colors group"
                data-testid="seller-profile-link"
              >
                <span className="text-sm text-[var(--theme-text-muted)]">Seller:</span>
                <div className="w-8 h-8 rounded-full bg-[#1E3A5F] flex items-center justify-center text-white text-sm font-bold overflow-hidden">
                  {seller?.profile_image ? (
                    <img src={seller.profile_image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    item.username?.[0]?.toUpperCase() || seller?.username?.[0]?.toUpperCase() || 'S'
                  )}
                </div>
                <span className="font-medium text-[var(--theme-text-primary)] group-hover:text-[#C9956C] transition-colors">
                  @{item.username || seller?.username || 'Seller'}
                </span>
                <span className="text-[var(--theme-text-muted)]">|</span>
                <span className="text-sm text-[var(--theme-text-secondary)]">
                  Trust: {item.seller_trust_score || seller?.trust_score || 50}
                </span>
                {memberYear && (
                  <>
                    <span className="text-[var(--theme-text-muted)]">|</span>
                    <span className="text-sm text-[var(--theme-text-secondary)]">
                      Member since {memberYear}
                    </span>
                  </>
                )}
                <ArrowRight className="w-4 h-4 ml-auto text-[var(--theme-text-muted)] group-hover:text-[#C9956C] transition-colors" />
              </Link>

              {/* Meta chips — Listed + (vehicle: tier + quality) | (other: condition) */}
              {(() => {
                const listed = formatRelativeTime(item.created_at);
                const isVehicle = item.category === 'vehicles' && item.vehicle_details;
                const tier = isVehicle ? (item.vehicle_details.listing_tier || 'basic') : null;
                const score = isVehicle ? item.vehicle_details.quality_score : null;

                // Quality chip color banding — emerald ≥85, gold 70–84, slate <70
                const qualityClass =
                  score == null ? '' :
                  score >= 85 ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-sm shadow-emerald-500/30' :
                  score >= 70 ? 'bg-gradient-to-r from-[#C9956C] to-[#B8845B] text-white shadow-sm shadow-[#C9956C]/30' :
                  'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200';

                // Condition chip color (non-vehicle)
                const conditionClass = {
                  excellent: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 ring-1 ring-emerald-200 dark:ring-emerald-800',
                  like_new:  'bg-[#1E3A5F]/10 dark:bg-[#C9956C]/10 text-[#1E3A5F] dark:text-[#C9956C] ring-1 ring-[#1E3A5F]/20 dark:ring-[#C9956C]/20',
                  good:      'bg-[#C9956C]/10 text-[#8a5f3a] dark:text-[#C9956C] ring-1 ring-[#C9956C]/30',
                  fair:      'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 ring-1 ring-orange-200 dark:ring-orange-800',
                  poor:      'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 ring-1 ring-red-200 dark:ring-red-800',
                }[item.condition] || 'bg-[var(--theme-hover)] text-[var(--theme-text-secondary)]';

                return (
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    {listed && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] text-xs font-medium ring-1 ring-[var(--theme-border)]">
                        <Clock className="w-3 h-3" />
                        Listed {listed}
                      </span>
                    )}
                    {isVehicle && tier === 'plus' && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-[#C9956C] via-[#D4A574] to-[#B8845B] text-white text-xs font-bold uppercase tracking-wider shadow-sm shadow-[#C9956C]/40">
                        <BadgeCheck className="w-3.5 h-3.5" />
                        WANTD Plus
                      </span>
                    )}
                    {isVehicle && tier !== 'plus' && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[var(--theme-hover)] text-[var(--theme-text-primary)] text-xs font-semibold uppercase tracking-wider ring-1 ring-[var(--theme-border)]">
                        WANTD Basic
                      </span>
                    )}
                    {isVehicle && score != null && (
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${qualityClass}`}>
                        Quality {score}/100
                      </span>
                    )}
                    {!isVehicle && conditionLabel && (
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold capitalize ${conditionClass}`}>
                        {conditionLabel}
                      </span>
                    )}
                  </div>
                );
              })()}

              {/* Description */}
              <div>
                <h3 className="font-semibold text-[var(--theme-text-primary)] mb-2">Description</h3>
                <p className="text-[var(--theme-text-secondary)] whitespace-pre-wrap leading-relaxed">
                  {item.description || 'No description provided.'}
                </p>
              </div>
            </div>

            {/* Vehicle details — spec sheet with PPSR request button at bottom */}
            {item.category === 'vehicles' && item.vehicle_details && (
              <VehicleDetailsCard vehicleDetails={item.vehicle_details} />
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              {(!user || user.id !== item.user_id) && (
                <Button
                  onClick={async () => {
                    if (!user) {
                      toast.error('Please sign in to message the seller');
                      navigate('/login');
                      return;
                    }
                    try {
                      const response = await api.post('/matches/start-conversation', null, {
                        params: { item_id: item.id }
                      });
                      navigate(`/messages/${response.data.match_id}`);
                    } catch (error) {
                      console.error('Failed to start conversation:', error);
                      toast.error(error.response?.data?.detail || 'Failed to start conversation');
                    }
                  }}
                  className="flex-1 bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white h-12 rounded-full"
                  data-testid="message-seller-btn"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Message Seller
                </Button>
              )}
              {user && user.id === item.user_id && (
                <Button
                  onClick={() => navigate(`/item/${item.id}/edit`)}
                  className="flex-1 bg-[var(--theme-card)] border border-[var(--theme-border)] text-[var(--theme-text-primary)] hover:bg-[var(--theme-hover)] h-12 rounded-full"
                  data-testid="edit-listing-btn"
                >
                  Edit Listing
                </Button>
              )}

              {/* Make an Offer — only for non-firm, non-free, available items that aren't owned by viewer */}
              {!isFree && !item.is_firm && item.status === 'available' && user && user.id !== item.user_id && (
                myOffer ? (
                  <Button
                    onClick={() => setShowOfferModal(true)}
                    className={`flex-1 h-12 rounded-full border-2 ${
                      myOffer.status === 'accepted'
                        ? 'bg-[#059669] hover:bg-[#047857] text-white border-[#059669]'
                        : myOffer.status === 'countered'
                        ? 'bg-[#C9956C] hover:bg-[#B8845B] text-white border-[#C9956C]'
                        : 'bg-transparent border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F]/5 dark:border-[#C9956C] dark:text-[#C9956C]'
                    }`}
                    data-testid="offer-status-btn"
                  >
                    <DollarSign className="w-5 h-5 mr-2" />
                    {myOffer.status === 'accepted'
                      ? `Accepted — Pay Now`
                      : myOffer.status === 'countered'
                      ? `Counter: $${(myOffer.amount_cents / 100).toFixed(2)}`
                      : `Offer Pending ($${(myOffer.amount_cents / 100).toFixed(2)})`}
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      if (!user) { toast.error('Please sign in to make an offer'); navigate('/login'); return; }
                      setShowOfferModal(true);
                    }}
                    className="flex-1 bg-transparent border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F]/5 dark:border-[#C9956C] dark:text-[#C9956C] h-12 rounded-full"
                    data-testid="make-offer-btn"
                  >
                    <DollarSign className="w-5 h-5 mr-2" />
                    Make an Offer
                  </Button>
                )
              )}
            </div>

            {/* Offer Modal */}
            {showOfferModal && (
              <div
                className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4"
                onClick={() => setShowOfferModal(false)}
              >
                <div
                  className="bg-[var(--theme-card)] rounded-2xl w-full max-w-md shadow-2xl"
                  onClick={e => e.stopPropagation()}
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-[var(--theme-text-primary)]">
                        {myOffer ? 'Your Offer' : 'Make an Offer'}
                      </h3>
                      <button
                        onClick={() => setShowOfferModal(false)}
                        className="p-1.5 rounded-full hover:bg-[var(--theme-hover)] text-[var(--theme-text-secondary)]"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    <p className="text-sm text-[var(--theme-text-secondary)] mb-1">
                      Listed at{' '}
                      <span className="font-semibold text-[var(--theme-text-primary)]">
                        ${Number(price).toFixed(2)}
                      </span>
                    </p>

                    {myOffer ? (
                      /* Show current offer status */
                      <div className="mt-4 space-y-4">
                        <div className="p-4 rounded-xl bg-[var(--theme-hover)] border border-[var(--theme-border)]">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm text-[var(--theme-text-secondary)]">Your offer</span>
                            <span className="font-bold text-[var(--theme-text-primary)]">
                              ${(myOffer.amount_cents / 100).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-[var(--theme-text-secondary)]">Status</span>
                            <span className={`text-sm font-semibold capitalize ${
                              myOffer.status === 'accepted' ? 'text-[#059669]' :
                              myOffer.status === 'countered' ? 'text-[#C9956C]' :
                              myOffer.status === 'declined' ? 'text-red-500' :
                              'text-[var(--theme-text-primary)]'
                            }`}>
                              {myOffer.status === 'countered'
                                ? `Counter: $${(myOffer.counter_amount_cents / 100).toFixed(2)}`
                                : myOffer.status}
                            </span>
                          </div>
                        </div>

                        {myOffer.status === 'accepted' && (
                          <Button
                            onClick={() => {
                              setShowOfferModal(false);
                              navigate(`/item/${item.id}/pay?offer_id=${myOffer.id}`);
                            }}
                            className="w-full bg-[#059669] hover:bg-[#047857] text-white h-12 rounded-full"
                          >
                            <DollarSign className="w-5 h-5 mr-2" />
                            Pay Now
                          </Button>
                        )}

                        {['pending', 'countered'].includes(myOffer.status) && (
                          <button
                            onClick={() => { setShowOfferModal(false); handleWithdrawOffer(); }}
                            className="w-full text-sm text-red-500 hover:text-red-600 py-2"
                          >
                            Withdraw Offer
                          </button>
                        )}
                      </div>
                    ) : (
                      /* Offer submission form */
                      <div className="mt-4 space-y-4">
                        <div>
                          <label className="text-sm font-medium text-[var(--theme-text-primary)] block mb-1.5">
                            Your offer amount (AUD)
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--theme-text-secondary)] font-medium">$</span>
                            <Input
                              type="number"
                              min="0.01"
                              step="0.01"
                              placeholder="0.00"
                              value={offerAmount}
                              onChange={e => setOfferAmount(e.target.value)}
                              className="pl-7 h-12 rounded-xl border-[var(--theme-border)]"
                              autoFocus
                            />
                          </div>
                        </div>
                        <Button
                          onClick={handleSubmitOffer}
                          disabled={offerSubmitting || !offerAmount}
                          className="w-full bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white h-12 rounded-full"
                        >
                          {offerSubmitting ? (
                            <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Sending...</span>
                          ) : 'Send Offer'}
                        </Button>
                        <p className="text-xs text-[var(--theme-text-muted)] text-center">
                          The seller will be notified and has 48 hours to respond.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Image Gallery - Shows SECOND on mobile, FIRST (left column) on desktop */}
          <div
            className="order-2 lg:order-1 space-y-3"
          >
            {/* Show all images vertically */}
            {images.map((img, idx) => (
              <div 
                key={idx}
                className={`relative rounded-xl overflow-hidden bg-[var(--theme-card)] border border-[var(--theme-border)] cursor-pointer group ${
                  images.length === 1 ? 'aspect-square' : 'aspect-[4/3]'
                }`}
                onClick={() => {
                  setLightboxIndex(idx);
                  setLightboxOpen(true);
                }}
                data-testid={`image-container-${idx}`}
              >
                <img 
                  src={img}
                  alt={`${item.title} - Image ${idx + 1}`}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                
                {/* Expand hint overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 rounded-full p-3">
                    <Expand className="w-6 h-6 text-white" />
                  </div>
                </div>
                
                {/* Image counter badge */}
                {images.length > 1 && (
                  <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-black/60 text-white text-xs font-medium">
                    {idx + 1} / {images.length}
                  </div>
                )}

                {/* Badges on first image only */}
                {idx === 0 && (
                  <div className="absolute top-3 left-3 flex flex-col gap-2">
                    {item.boost_tier && (
                      <span className="px-3 py-1 rounded-full bg-[#C9956C]/90 backdrop-blur-sm text-xs font-bold uppercase tracking-wider text-white">
                        PROMOTED
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Image Lightbox */}
      <ImageLightbox
        images={images}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        alt={item?.title}
      />

      {/* Report Modal */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        itemId={item?.id}
        itemType="inventory"
        itemTitle={item?.title}
      />
    </Layout>
  );
}
